var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"870d0929-3fc0-41cc-aee9-975aa7c778cf"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "";

// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs';
import * as Spotlight from '@spotlightjs/spotlight';
import type { Event, EventHint } from '@sentry/types';

const spotlightEnabled =
  process.env.NODE_ENV === 'development' &&
  process.env.NEXT_PUBLIC_ENABLE_SPOTLIGHT === 'true';

const ignoredClientErrorPatterns = [
  // Known iOS WebView/native-bridge noise. This does not originate in the
  // marketplace codebase and currently floods Sentry as an unhandled error.
  /window\.webkit\.messageHandlers/,
];

function eventMessageCandidates(event: Event, hint?: EventHint): string[] {
  const candidates: string[] = [];
  if (event.message) candidates.push(event.message);

  event.exception?.values?.forEach((exception) => {
    if (exception.value) candidates.push(exception.value);
    if (exception.type) candidates.push(exception.type);
  });

  const originalException = hint?.originalException;
  if (originalException instanceof Error) {
    candidates.push(originalException.message);
  } else if (typeof originalException === 'string') {
    candidates.push(originalException);
  }

  return candidates;
}

function shouldDropClientEvent(event: Event, hint?: EventHint): boolean {
  return eventMessageCandidates(event, hint).some((message) =>
    ignoredClientErrorPatterns.some((pattern) => pattern.test(message))
  );
}

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

  // Set environment from env var or default to 'local'
  environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || 'local',

  // Adjust this value in production, or use tracesSampler for greater control
  tracesSampleRate: 1,

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  ignoreErrors: ignoredClientErrorPatterns,

  beforeSend(event, hint) {
    if (shouldDropClientEvent(event, hint)) return null;
    return event;
  },

  replaysOnErrorSampleRate: 1.0,

  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,

  // You can remove this option if you're not planning to use the Sentry Session Replay feature:
  integrations: [
    Sentry.replayIntegration({
      // Additional Replay configuration goes in here, for example:
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
});

if (spotlightEnabled) {
  Spotlight.init();
}
